import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Send, Inbox } from 'lucide-react';
import { notificationSchema, NotificationFormData } from '../utils/validators';
import { sendBroadcast } from '../services/api';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import toast from 'react-hot-toast';

const targetLabels: Record<string, string> = {
  all: 'All Users',
  trial: 'Trial Users',
  active: 'Active Subscribers',
  expired: 'Expired Users',
};

export default function NotificationsPage() {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<NotificationFormData>({
    resolver: zodResolver(notificationSchema),
    defaultValues: { targetGroup: 'all', title: '', body: '' },
  });

  const title = watch('title');
  const body = watch('body');
  const broadcast = useMutation({
    mutationFn: sendBroadcast,
    onSuccess: (data) => {
      toast.success(`Notification sent to ${data.sent} users!`);
      reset();
    },
    onError: () => toast.error('Failed to send notification'),
  });

  const onSubmit = (data: NotificationFormData) => {
    if (
      window.confirm(
        `Send notification to ${targetLabels[data.targetGroup]}?`
      )
    ) {
      broadcast.mutate(data);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Send Broadcast */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-5">Send Notification</h3>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Target Group */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              Target Group
            </label>
            <select
              {...register('targetGroup')}
              className="w-full bg-surface border-0 rounded-btn px-4 py-2.5 text-sm text-text-primary focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">All Users</option>
              <option value="trial">Trial Users</option>
              <option value="active">Active Subscribers</option>
              <option value="expired">Expired Users</option>
            </select>
          </div>

          {/* Title */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-text-primary">Title</label>
              <span className="text-[11px] text-text-hint">{title.length}/50</span>
            </div>
            <input
              {...register('title')}
              maxLength={50}
              placeholder="Notification title"
              className="w-full bg-surface border-0 rounded-btn px-4 py-2.5 text-sm text-text-primary placeholder:text-text-hint focus:ring-2 focus:ring-primary/20"
            />
            {errors.title && <p className="mt-1 text-xs text-error">{errors.title.message}</p>}
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-text-primary">Body</label>
              <span className="text-[11px] text-text-hint">{body.length}/150</span>
            </div>
            <textarea
              {...register('body')}
              maxLength={150}
              rows={3}
              placeholder="Notification message"
              className="w-full bg-surface border-0 rounded-btn px-4 py-2.5 text-sm text-text-primary placeholder:text-text-hint focus:ring-2 focus:ring-primary/20 resize-none"
            />
            {errors.body && <p className="mt-1 text-xs text-error">{errors.body.message}</p>}
          </div>

          {/* Preview */}
          <div>
            <p className="text-xs text-text-hint mb-2">Notification Preview</p>
            <div className="bg-slate-100 rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-5 h-5 rounded bg-primary flex items-center justify-center">
                  <span className="text-white text-[8px] font-bold">P</span>
                </div>
                <span className="text-[11px] text-text-hint font-medium">
                  Plus FTP & SFTP Server
                </span>
              </div>
              <p className="text-sm font-semibold text-text-primary">
                {title || 'Notification Title'}
              </p>
              <p className="text-sm text-text-secondary mt-0.5">
                {body || 'Notification body will appear here...'}
              </p>
            </div>
          </div>

          <Button
            type="submit"
            loading={broadcast.isPending}
            icon={<Send className="w-4 h-4" />}
            className="w-full h-11"
          >
            Send Notification
          </Button>
        </form>
      </Card>

      {/* Sent History */}
      <Card className="overflow-hidden">
        <div className="p-6 border-b border-border">
          <h3 className="text-base font-semibold text-text-primary">Sent History</h3>
        </div>
        <EmptyState
          icon={<Inbox className="w-12 h-12" />}
          title="No notifications sent yet"
          subtitle="Sent notification history coming soon"
        />
      </Card>
    </div>
  );
}
